import React, { useEffect, useState } from 'react';
import { Button } from '../UI/Button';
import type { AgentGraphNodeType } from '../../types/agentPlatform';
import {
  graphNodeTypeColor,
  graphNodeTypeLabel,
  parseGraphDefinition,
  serializeGraphDefinition,
  type AgentGraphDefinitionModel,
  type AgentGraphEdgeModel,
  type AgentGraphNodeModel,
} from '../../utils/agentGraphUtils';
import { apTheme } from '../../utils/agentPlatformTheme';


type Props = {
  definitionJson: string;
  onChange: (json: string) => void;
};

const NODE_TYPES: AgentGraphNodeType[] = ['LlmRouter', 'Tool', 'Condition', 'Respond'];

export const AgentGraphVisualEditor: React.FC<Props> = ({ definitionJson, onChange }) => {
  const [model, setModel] = useState<AgentGraphDefinitionModel>(() =>
    parseGraphDefinition(definitionJson)
  );

  useEffect(() => {
    setModel(parseGraphDefinition(definitionJson));
  }, [definitionJson]);

  const commit = (next: AgentGraphDefinitionModel) => {
    setModel(next);
    onChange(serializeGraphDefinition(next));
  };

  const updateNode = (index: number, patch: Partial<AgentGraphNodeModel>) => {
    const nodes = [...model.nodes];
    nodes[index] = { ...nodes[index], ...patch };
    commit({ ...model, nodes });
  };

  const addNode = (type: AgentGraphNodeType) => {
    const id = `${type.toLowerCase()}-${Date.now()}`;
    commit({
      ...model,
      nodes: [...model.nodes, { id, type, config: {} }],
    });
  };

  const removeNode = (id: string) => {
    commit({
      ...model,
      entryNodeId: model.entryNodeId === id ? '' : model.entryNodeId,
      nodes: model.nodes.filter(n => n.id !== id),
      edges: model.edges.filter(e => e.from !== id && e.to !== id),
      settings: {
        ...model.settings,
        fallbackNodeId: model.settings.fallbackNodeId === id ? null : model.settings.fallbackNodeId,
      },
    });
  };

  const addEdge = () => {
    const from = model.nodes[0]?.id ?? '';
    const to = model.nodes[1]?.id ?? from;
    const edge: AgentGraphEdgeModel = { from, to, condition: { action: 'default' } };
    commit({ ...model, edges: [...model.edges, edge] });
  };

  const updateEdge = (index: number, patch: Partial<AgentGraphEdgeModel>) => {
    const edges = [...model.edges];
    edges[index] = { ...edges[index], ...patch };
    commit({ ...model, edges });
  };

  const removeEdge = (index: number) => {
    commit({ ...model, edges: model.edges.filter((_, i) => i !== index) });
  };

  return (
    <div className="space-y-6">
      <section className="grid sm:grid-cols-3 gap-3">
        <div>
          <label className="block text-xs font-medium text-stone-700 mb-1">Nó de entrada</label>
          <select
            className={apTheme.input}
            value={model.entryNodeId}
            onChange={e => commit({ ...model, entryNodeId: e.target.value })}
          >
            <option value="">—</option>
            {model.nodes.map(n => (
              <option key={n.id} value={n.id}>
                {n.id}
              </option>
            ))}
          </select>
        </div>
        <div>
          <label className="block text-xs font-medium text-stone-700 mb-1">Max steps</label>
          <input
            type="number"
            min={1}
            className={apTheme.input}
            value={model.settings.maxSteps}
            onChange={e =>
              commit({
                ...model,
                settings: { ...model.settings, maxSteps: Number(e.target.value) || 20 },
              })
            }
          />
        </div>
        <div>
          <label className="block text-xs font-medium text-stone-700 mb-1">Fallback</label>
          <select
            className={apTheme.input}
            value={model.settings.fallbackNodeId ?? ''}
            onChange={e =>
              commit({
                ...model,
                settings: { ...model.settings, fallbackNodeId: e.target.value || null },
              })
            }
          >
            <option value="">—</option>
            {model.nodes.map(n => (
              <option key={n.id} value={n.id}>
                {n.id}
              </option>
            ))}
          </select>
        </div>
      </section>

      <section>
        <div className="flex flex-wrap items-center justify-between gap-2 mb-3">
          <h3 className="text-sm font-semibold text-stone-900">Nós</h3>
          <div className="flex flex-wrap gap-1">
            {NODE_TYPES.map(t => (
              <Button key={t} variant="secondary" size="sm" type="button" onClick={() => addNode(t)}>
                + {graphNodeTypeLabel(t)}
              </Button>
            ))}
          </div>
        </div>
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-3">
          {model.nodes.map((node, index) => (
            <div
              key={node.id}
              className={`border-2 rounded-xl p-3 shadow-sm ${graphNodeTypeColor(node.type)}`}
            >
              <div className="flex justify-between items-start mb-2">
                <span className="text-xs font-semibold uppercase tracking-wide text-gray-600">
                  {graphNodeTypeLabel(node.type)}
                </span>
                <button
                  type="button"
                  className="text-red-600 text-xs hover:underline"
                  onClick={() => removeNode(node.id)}
                >
                  remover
                </button>
              </div>
              <input
                className="w-full text-sm font-mono border rounded px-2 py-1 mb-2 bg-white"
                value={node.id}
                onChange={e => updateNode(index, { id: e.target.value })}
              />
              {node.type === 'Tool' && (
                <input
                  className="w-full text-xs border rounded px-2 py-1 mb-1 bg-white"
                  placeholder="toolCode"
                  value={String(node.config.toolCode ?? '')}
                  onChange={e =>
                    updateNode(index, { config: { ...node.config, toolCode: e.target.value } })
                  }
                />
              )}
              {node.type === 'LlmRouter' && (
                <input
                  className="w-full text-xs border rounded px-2 py-1 bg-white"
                  placeholder="Descrição"
                  value={String(node.config.description ?? '')}
                  onChange={e =>
                    updateNode(index, { config: { ...node.config, description: e.target.value } })
                  }
                />
              )}
            </div>
          ))}
        </div>
      </section>

      <section>
        <div className="flex items-center justify-between mb-3">
          <h3 className="text-sm font-semibold text-gray-800">Arestas</h3>
          <Button variant="secondary" size="sm" type="button" onClick={addEdge}>
            + Aresta
          </Button>
        </div>
        <div className="space-y-2">
          {model.edges.length === 0 && (
            <p className="text-sm text-gray-500">Nenhuma aresta definida.</p>
          )}
          {model.edges.map((edge, index) => (
            <div key={index} className="flex flex-wrap items-center gap-2 text-sm bg-gray-50 p-2 rounded">
              <select
                className="border rounded px-2 py-1"
                value={edge.from}
                onChange={e => updateEdge(index, { from: e.target.value })}
              >
                {model.nodes.map(n => (
                  <option key={n.id} value={n.id}>
                    {n.id}
                  </option>
                ))}
              </select>
              <span className="text-gray-400">→</span>
              <select
                className="border rounded px-2 py-1"
                value={edge.to}
                onChange={e => updateEdge(index, { to: e.target.value })}
              >
                {model.nodes.map(n => (
                  <option key={n.id} value={n.id}>
                    {n.id}
                  </option>
                ))}
              </select>
              <input
                className="flex-1 min-w-[120px] border rounded px-2 py-1 font-mono text-xs"
                placeholder='condition JSON {"action":"respond"}'
                value={edge.condition ? JSON.stringify(edge.condition) : ''}
                onChange={e => {
                  try {
                    const condition = e.target.value ? JSON.parse(e.target.value) : null;
                    updateEdge(index, { condition });
                  } catch {
                    /* aguarda JSON válido */
                  }
                }}
              />
              <button
                type="button"
                className="text-red-600 text-xs"
                onClick={() => removeEdge(index)}
              >
                ×
              </button>
            </div>
          ))}
        </div>
      </section>

      <div className="flex flex-wrap gap-2 items-center text-xs text-gray-500 border-t pt-3">
        {model.nodes.map((node, i) => (
          <React.Fragment key={node.id}>
            {i > 0 && <span>·</span>}
            <span className="font-mono">{node.id}</span>
          </React.Fragment>
        ))}
      </div>
    </div>
  );
};
